import { Mark } from '@tiptap/core'
import { normalizeRunColor, rPrToCss, rStyleOf, RUN_HIGHLIGHTS, runToggleOffClasses, shadingTone } from '../ooxmlCss.js'

/**
 * The verbatim run-properties passthrough. The backend keeps the FULL
 * <w:rPr> on this mark; recognized toggles (bold/italic/underline/strike)
 * are separate first-class marks, and everything else — font, size, color,
 * highlight, caps — is surfaced as derived CSS here. The rPr itself is never
 * modified client-side: a colour or highlight the editor sets is a delta attr
 * next to it (RED-317 PR 3 contract §1), null = untouched.
 */
export const OoxmlRun = Mark.create({
  name: 'ooxmlRun',

  addAttributes() {
    return {
      rPr: { default: null, rendered: false },
      // 'auto' or 6-hex → w:color, replacing any colour incl. theme colours.
      color: { default: null, rendered: false },
      // An ST_HighlightColor name → w:highlight; 'none' removes it.
      highlight: { default: null, rendered: false },
    }
  },

  // Only the Word paste transform's spans carry colour into a paste
  // (../wordPaste.js): data-ooxml-color (6-hex or 'auto') and
  // data-ooxml-highlight (an ST_HighlightColor name or 'none').
  parseHTML() {
    return [
      {
        tag: 'span',
        getAttrs: (element) => {
          const color = normalizeRunColor(element.getAttribute('data-ooxml-color'))
          const value = element.getAttribute('data-ooxml-highlight')
          const highlight = value === 'none' || RUN_HIGHLIGHTS.includes(value) ? value : null
          return color === null && highlight === null ? false : { rPr: null, color, highlight }
        },
      },
    ]
  },

  addCommands() {
    return {
      /**
       * Text colour of the selection ('auto' or 6-hex; null clears the
       * delta). setMark merges into each run's own ooxmlRun mark, so the
       * verbatim rPr stays; a run without one gets a mark with rPr null.
       */
      setRunColor:
        (color) =>
        ({ commands }) => {
          const value = color === null ? null : normalizeRunColor(color)
          if (color !== null && value === null) return false
          return commands.setMark(this.name, { color: value })
        },
      /** Highlight of the selection (a RUN_HIGHLIGHTS name, 'none', or null to clear the delta). */
      setRunHighlight:
        (highlight) =>
        ({ commands }) => {
          if (highlight !== null && highlight !== 'none' && !RUN_HIGHLIGHTS.includes(highlight)) return false
          return commands.setMark(this.name, { highlight })
        },
    }
  },

  renderHTML({ mark }) {
    const style = rPrToCss(mark.attrs.rPr, { color: mark.attrs.color, highlight: mark.attrs.highlight })
    const rStyle = mark.attrs.rPr ? rStyleOf(mark.attrs.rPr) : null
    // studio-run: the run moves its glyphs to Word's baseline (studio.css, lineMetrics.js);
    // studio-run-b-off / -i-off: bold or italic switched off in the rPr (beats a style's).
    const toggledOff = mark.attrs.rPr ? runToggleOffClasses(mark.attrs.rPr) : ''
    // data-fill-tone: the run's own shading sets Word's automatic text colour on it (studio.css).
    const attrs = {
      class: toggledOff ? `studio-run ${toggledOff}` : 'studio-run',
      'data-r-style': rStyle ?? undefined,
      'data-fill-tone': (mark.attrs.rPr && shadingTone(mark.attrs.rPr)) || undefined,
    }
    if (style) attrs.style = style
    return ['span', attrs, 0]
  },
})

/**
 * Word hyperlink projection: linkId preserves the original w:hyperlink
 * element boundaries (adjacent same-target links must not merge on
 * compile), attributes carries the element's attributes verbatim.
 */
export const StudioLink = Mark.create({
  name: 'link',

  // A link mark spans whole runs; keep boundaries exact.
  inclusive: false,

  addAttributes() {
    return {
      linkId: { default: null, rendered: false },
      href: { default: null, rendered: false },
      attributes: { default: null, rendered: false },
    }
  },

  renderHTML({ mark }) {
    return [
      'a',
      {
        class: 'studio-link',
        href: mark.attrs.href ?? undefined,
        rel: 'noopener noreferrer',
        target: '_blank',
      },
      0,
    ]
  },
})
